import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ControlPanelHeader from '../ControlPanelHeader/ControlPanelHeader';
import ControlPanelMainControls from '../ControlPanelMainControls/ControlPanelMainControls';
import ControlPanelFooter from '../ControlPanelFooter/ControlPanelFooter';
import './ControlPanel.scss';
import useRoomState from '../../hooks/useRoomState/useRoomState';
import { TaskType } from '../../commands/practitioner/enums/TaskType';
import BackendService from '../../services/BackendService';
import { startTimer, endTimer, metrics, mediaKey, taskKey, callKey } from '../../metrics/sentryMetrics';
import ControlPanelSessionTab from '../ControlPanelSessionTab/ControlPanelSessionTab';
import ControlPanelTaskTab from '../ControlPanelTaskTab/ControlPanelTaskTab';
import { useTranslation } from 'react-i18next';
import { SphereTrajectoryOption } from '../../commands/practitioner/enums/SphereTrajectoryOption';
import { CommandPrefixes } from '../../commands/practitioner/enums/CommandPrefixes';
import { Beforeunload } from 'react-beforeunload';
import moment from 'moment';
import { State } from '../../commands/practitioner/enums/State';
import usePractitionerControlsContext from '../../hooks/usePractitionerControlsContext/usePractitionerControlsContext';
import useAppContext from '../../hooks/useAppContext/useAppContext';
import { MediaTypes } from '../../commands/practitioner/enums/MediaTypes';
import { MediaInteractions } from '../../commands/practitioner/enums/MediaInteractions';
import { config } from '../PhaserApp/components/application/config/TherapistSceneConfig';
import TaskScene from '../PhaserApp/components/application/scene/TaskScene';
import { SourceProjection } from '../../commands/practitioner/enums/SourceProjection';
import useMediaContext from '../../hooks/useMediaContextHook/useMediaContext';
import TabOptionsToogle from '../TabOptionsToogle/TabOptionsToogle';
import { Calculation } from '../PhaserApp/enums/CalculationSettings';
import {
  simultaneouslyMediaTaskSupportedVersion,
  isVersionLessThan,
  newTaskSupportedVersion,
  ThemeSupportedVersion,
} from '../../utils/versionCheck';
import { ColorSwitchOptions } from '../../commands/practitioner/enums/ColorSwitchOptions';
import { StroopConfigurationOptions } from '../../commands/practitioner/enums/StroopConfigurationOptions';

export function getParameterByName(name: string, url: string = '') {
  if (!url) url = window.location.href;
  name = name.replace(/[\[\]]/g, '\\$&');
  let regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
    results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

const addComma = (value: any) => {
  return `${value},`;
};

const addSeed = (value: any) => {
  return `${value}`;
};

const SESSION_TAB = 'Sessielog';
const TASK_TAB = 'Taakcomponenten';

interface ContainerProps {
  ConnectToRoom: Function;
  StopCall: Function;
  location?: any;
  practitionerIsConnected: boolean;
  setPractitionerIsConnected: Dispatch<SetStateAction<boolean>>;
  currentState: any;
  isCallActive: boolean;
}

const ControlPanel: React.FC<ContainerProps> = ({
  ConnectToRoom,
  StopCall,
  practitionerIsConnected,
  setPractitionerIsConnected,
  currentState,
  isCallActive,
}) => {
  const { t } = useTranslation();
  const { isConnected: isDeviceConnected, waitingForDeviceCheck } = currentState || {};
  const isWaitingForDeviceCheck = waitingForDeviceCheck === true || currentState?.isWaitingForDeviceCheck === true;
  const {
    eyeMovement,
    speed,
    trajectory,
    interactive,
    autoSpeed,
    setAutoSpeed,
    reactionVisual,
    reactionAudio,
    colorInterval,
    calculationDifficulty,
    sound,
    soundClicks,
    soundPlops,
    soundBuzzer,
    notes,
    vocScores,
    sudScores,
    setIsPractitionerReactPlayerStarted: setMediaIsStarted,
    taskHasStarted,
    setTaskHasStarted,
    setGame,
    stroopConfiguration,
    reactionAnimation,
    theme,
  } = usePractitionerControlsContext();

  const { sessionId, treatmentId, hasDevice } = useAppContext();

  const version = typeof currentState?.version === 'string' ? currentState.version : undefined;
  const isDeviceThemeSupported = hasDevice && !!version && !isVersionLessThan(version, ThemeSupportedVersion);
  const shouldShowThemeSelector = !hasDevice || isDeviceThemeSupported;
  const shouldSendThemeToBackend = !hasDevice || isDeviceThemeSupported;

  // Tabs
  const tabOptions = [TASK_TAB, SESSION_TAB];
  const [activeTab, setActiveTab] = useState('Taakcomponenten');

  // Room properties
  const roomState = useRoomState();
  const isConnected = roomState !== 'disconnected';
  const [clientIsConnected, setClientIsConnected] = useState(false);
  const useLegacy = hasDevice && isVersionLessThan(currentState.version ?? '0.0.0', newTaskSupportedVersion);
  const handleClick = (option: string) => {
    setActiveTab(option);
  };

  function getRandomInt(max: number) {
    return Math.floor(Math.random() * max);
  }

  const [corrects, setCorrects] = useState(0);
  const [faults, setFaults] = useState(0);
  const [initialResults, setInitialResults] = useState([] as string[]);
  const [totalResults, setTotalResults] = useState(() => [] as string[]);
  const [taskStartedOnce, setTaskStartedOnce] = useState<boolean>(false);
  const [gameIsLoading, setGameIsLoading] = useState<boolean>(true);
  // Command settings
  const [taskSnapoutClicked, setTaskSnapoutClicked] = useState<boolean>(false);
  const isReactionTask =
    reactionVisual || reactionAudio || calculationDifficulty !== 0 || stroopConfiguration !== 0 || reactionAnimation;
  // therapist.calledBy = 'therapist'
  //phaserGame
  const phaserRef = useRef<Phaser.Game | null>(null);
  const maxBallSpeed = 2;
  const speedMultiplier = 1.75;
  const prevLengthRef = useRef<number>(0);
  const firstRender = useRef(false);

  const isOldVersion =
    typeof currentState.version === 'string' &&
    isVersionLessThan(currentState.version, simultaneouslyMediaTaskSupportedVersion);
  const isFullCommandToSend =
    !!currentState.version && !isVersionLessThan(currentState.version, newTaskSupportedVersion);
  const commandToSend = useMemo(() => {
    // Create the command based on all settings.
    let value = '';

    //Should I add command here?

    value += addComma(taskHasStarted ? TaskType.Enabled : TaskType.Disabled);
    value += addComma(taskSnapoutClicked ? State.ON : eyeMovement ? State.ON : State.OFF);
    value += addComma(taskSnapoutClicked ? maxBallSpeed * speedMultiplier : speed * speedMultiplier);
    value += addComma(taskSnapoutClicked ? SphereTrajectoryOption.Bouncing : trajectory);
    value += addComma(taskSnapoutClicked ? State.ON : interactive ? State.ON : State.OFF);
    value += addComma(taskSnapoutClicked ? State.OFF : autoSpeed ? State.ON : State.OFF);
    value += addComma(taskSnapoutClicked ? State.ON : reactionVisual ? State.ON : State.OFF);
    value += addComma(taskSnapoutClicked ? State.ON : reactionAudio ? State.ON : State.OFF);
    value += addComma(taskSnapoutClicked ? ColorSwitchOptions.Automatically : colorInterval);
    value += addComma(taskSnapoutClicked ? Calculation.easy : calculationDifficulty);
    value += addComma(taskSnapoutClicked ? State.ON : sound ? State.ON : State.OFF);
    value += addComma(taskSnapoutClicked ? State.ON : soundClicks ? State.ON : State.OFF);
    value += addComma(taskSnapoutClicked ? State.ON : soundPlops ? State.ON : State.OFF);
    value += addComma(taskSnapoutClicked ? 5 : soundBuzzer);
    if (hasDevice && isFullCommandToSend) {
      value += addComma(taskSnapoutClicked ? State.ON : reactionAnimation ? State.ON : State.OFF);
      value += addComma(stroopConfiguration);
    } else if (hasDevice && !isFullCommandToSend) {
      value += addComma(State.OFF);
      value += addComma(StroopConfigurationOptions.None);
    } else {
      value += addComma(taskSnapoutClicked ? State.ON : reactionAnimation ? State.ON : State.OFF);
      value += addComma(taskSnapoutClicked ? StroopConfigurationOptions.None : stroopConfiguration);
    }
    value += addSeed(getRandomInt(1_000_000));

    return value;
  }, [
    taskHasStarted,
    eyeMovement,
    speed,
    trajectory,
    interactive,
    autoSpeed,
    reactionVisual,
    reactionAudio,
    colorInterval,
    calculationDifficulty,
    sound,
    soundClicks,
    soundPlops,
    soundBuzzer,
    taskSnapoutClicked,
    stroopConfiguration,
    reactionAnimation,
    isFullCommandToSend,
    hasDevice,
  ]);
  const { setSelectedMarket } = useMediaContext();

  useEffect(() => {
    const firstLoadMarket = localStorage.getItem('i18nextLng');

    if (firstLoadMarket === 'en') {
      setSelectedMarket('nl');
    } else {
      if (firstLoadMarket) setSelectedMarket(firstLoadMarket);
    }
  }, []);

  useEffect(() => {
    if (!firstRender.current) {
      firstRender.current = true;
      return;
    } else {
      let booleanTheme = !!theme;

      const therapist = phaserRef?.current?.scene.keys.therapist as TaskScene;
      therapist.updateTaskBasedOnEncodedCommand(
        BackendService.generateEscapedTaskSettingsCommandString(commandToSend),
        booleanTheme
      );
      if (shouldSendThemeToBackend) {
        BackendService.sendThemeCommand(sessionId, treatmentId, booleanTheme);
      }
    }
  }, [theme]);

  useEffect(() => {
    if (!taskStartedOnce) return;
    if (!phaserRef.current) {
      console.warn('Game not initialized yet. Skipping task start/stop.');
      return;
    }

    const booleanTheme = !!theme;
    const therapist = phaserRef.current.scene.keys.therapist as TaskScene;
    therapist.updateTaskBasedOnEncodedCommand(
      BackendService.generateEscapedTaskSettingsCommandString(commandToSend),
      booleanTheme,
      useLegacy
    );

    (async () => {
      try {
        await BackendService.sendTaskSettingsCommandToClient(sessionId, treatmentId, commandToSend);
      } catch (err) {
        console.error('Failed to sync task settings and theme', err);
      }
    })();
  }, [commandToSend, taskStartedOnce, sessionId, treatmentId, useLegacy]);

  useEffect(() => {
    if (!phaserRef.current) {
      console.warn('Game not initialized yet. Skipping theme update.');
      return;
    }
    const therapist = phaserRef?.current?.scene.keys.therapist as TaskScene;
    if (therapist === undefined) return;
    if (taskHasStarted) {
      therapist.startTask();
    } else if (taskStartedOnce) {
      therapist.stopTask();
    }
  }, [taskHasStarted, phaserRef.current]);

  const handleToggleTaskClicked = () => {
    setTaskSnapoutClicked(false);
    const newStartedTaskValue = !taskHasStarted;
    if (newStartedTaskValue) {
      // Start E2E timer; client will ack via MLSR task://start
      try {
        startTimer(taskKey());
      } catch {}
      setTaskStartedOnce(true);
      // When a task is started on the device, the media should be paused for the practitioner
      if (hasDevice && isOldVersion) {
        setMediaIsStarted(false);
      }
    }
    setTaskHasStarted(newStartedTaskValue);
    setInitialResults(totalResults);
  };

  const handleSnapoutClicked = () => {
    setTaskSnapoutClicked(!taskSnapoutClicked);
  };

  const parseFeedbackResults = (result: string[]) => {
    const allFeedback = (result || []).filter((reactionFeedback: string) =>
      reactionFeedback.includes(CommandPrefixes.REACTION_FEEDBACK)
    );
    let correctsValue = 0;
    let faultsValue = 0;
    allFeedback.forEach(feedback => {
      if (feedback.includes('1')) {
        correctsValue++;
      } else {
        faultsValue++;
      }
    });
    setCorrects(correctsValue);
    setFaults(faultsValue);
  };

  useEffect(() => {
    const onLoad = () => setGameIsLoading(false);

    if (document.readyState === 'complete') {
      onLoad();
    } else {
      window.addEventListener('load', onLoad);
      return () => window.removeEventListener('load', onLoad);
    }
  }, []);

  useEffect(() => {
    if (!gameIsLoading) {
      phaserRef.current = new Phaser.Game(config);
      setGame(phaserRef.current);
    }
  }, [gameIsLoading]);

  useEffect(() => {
    // track where we left off for APD‐type commands
    let indexLastHandledCommand = -1;
    let firstCall = true;

    // the polling function
    const pollCommands = async () => {
      if (!phaserRef.current) {
        console.warn('Polling skipped: Game not initialized');
        return;
      }
      try {
        const result = await BackendService.fetchCurrentCommandsForPractitioner(sessionId, treatmentId);
        if (!result) {
          return;
        }
        // Update the total number of reactions we received
        setTotalResults(result);

        const lastIdx = result.length - 1;
        const lastCommand = result[lastIdx];

        if (firstCall) {
          indexLastHandledCommand = result.length;
          firstCall = false;
        }

        if (lastCommand.startsWith('TC:')) {
          const therapist = phaserRef?.current?.scene.keys.therapist as TaskScene;
        }

        if (lastCommand.startsWith('RPF')) {
          let booleanTpeme = !!theme;
          const therapist = phaserRef?.current?.scene.keys.therapist as TaskScene;
          therapist.updateTaskBasedOnEncodedCommand(lastCommand, booleanTpeme);

          if (result.length > prevLengthRef.current) {
            therapist.ClientToPractitionerTaskAnswer(lastCommand);
          }
        }

        // Look for load-status reports to finalize timers (media/task/call)
        // Format: MLSR:<status>,<progress>,"<uri>"
        if (lastCommand.startsWith('MLSR:')) {
          try {
            const [, payload] = lastCommand.split('MLSR:');
            const parts = payload.split(',');
            const statusNum = Number(parts[0]);
            const rawUri = parts.slice(2).join(',');
            const m = rawUri.match(/"(.+)"/);
            const uri = decodeURIComponent(m ? m[1] : rawUri.replace(/"/g, ''));

            const status = statusNum === 1 ? 'successful' : statusNum === 2 ? 'failure' : String(statusNum);

            // media play E2E timing keyed by URI
            const finishedMedia = endTimer(mediaKey(uri), 'media.play.propagation_ms', {
              status,
              sessionId,
              treatmentId,
            });

            // task and call acknowledgements use special URIs
            if (uri.startsWith('task://start')) {
              endTimer(taskKey(), 'task.start.propagation_ms', { status, sessionId, treatmentId });
            }
            if (uri.startsWith('call://connected')) {
              endTimer(callKey(), 'call.connect.propagation_ms', { status, sessionId, treatmentId });
            }

            // Also emit a simple counter for status categories
            metrics.count('mlsr.received', 1, {
              status,
              uri_type: uri.split('://')[0] ?? 'media',
              sessionId,
              treatmentId,
            });
          } catch (e) {
            // ignore parse errors
          }
        }

        // remember how many we’ve seen so far
        prevLengthRef.current = result.length;
      } catch (e) {
        console.error(e);
      }
    };

    const intervalId = window.setInterval(() => {
      void pollCommands();
    }, 300);

    return () => {
      clearInterval(intervalId);
    };
  }, [theme]);

  useEffect(() => {
    if (!taskHasStarted) {
      setInitialResults(totalResults);
    }
  }, [totalResults, taskHasStarted, setInitialResults]);

  useEffect(() => {
    const startLengthToSlice = initialResults.length;
    const endLengthToSlice = totalResults.length;
    let arrayToParse = totalResults.slice(startLengthToSlice, endLengthToSlice);
    parseFeedbackResults(arrayToParse);
  }, [initialResults, totalResults]);

  useEffect(() => {
    setCorrects(0);
    setFaults(0);
  }, [taskHasStarted]);

  const combinedAttributesToSend = useMemo(() => {
    return {
      session_completed: '1', // Hardcoded toggle the correct prop
      notes: [notes],
      sud_array: sudScores.map((item: any) => item.value.toString()),
      validity_of_cognition_array: vocScores.map((item: any) => item.value.toString()),
    };
  }, [notes, vocScores, sudScores]);

  const parseUmirCommands = (commands: string[]) => {
    const umirCommands = commands.filter((command: string) =>
      command.includes(CommandPrefixes.USER_INTERACTION_MEDIA_REPORT)
    );
    const commandToUse = (umirCommands || [])[0];
    if (!commandToUse || !hasDevice) {
      return;
    }
    if (commandToUse.includes(MediaInteractions.Stopped.toString())) {
      setMediaIsStarted(false);
    } else if (commandToUse.includes(MediaInteractions.Resumed.toString())) {
      setMediaIsStarted(true);
    }
  };

  useEffect(() => {
    const startLengthToSlice = initialResults.length;
    const endLengthToSlice = totalResults.length;
    let arrayToParse = totalResults.slice(startLengthToSlice, endLengthToSlice);
    parseUmirCommands(arrayToParse);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialResults, totalResults]);

  // @ts-ignore
  const triggerCloseOnUnity = useCallback(
    (e: BeforeUnloadEvent) => {
      try {
        setTaskHasStarted(false);
        const endDate = moment()
          .utc()
          .toISOString();
        BackendService.updateSessionData(sessionId, treatmentId, combinedAttributesToSend, endDate)
          .then(async () => {
            try {
              await BackendService.sendLoadMedia(
                sessionId,
                treatmentId,
                '',
                MediaTypes.VIDEO,
                SourceProjection.Flat,
                'Off'
              );
              await BackendService.sendTaskSettingsCommandToClient(sessionId, treatmentId, commandToSend);
              await BackendService.sendMediaVisibility(sessionId, treatmentId, '', false);
            } catch (err) {
              console.error('Failed to reset the media and clientState.');
            }
          })
          .catch(error => {
            console.error('Failed to send last update session data and ending the session.', error);
          });
      } catch (errorValue) {
        console.error('failed to update the session data', errorValue);
      } finally {
        e.preventDefault();
        return 'Are you sure you wish to close this app?';
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [notes, sudScores, vocScores, combinedAttributesToSend]
  );

  const renderCorrectTab = useMemo(() => {
    switch (activeTab) {
      case SESSION_TAB:
        return <ControlPanelSessionTab sessionUUID={sessionId} treatmentUUID={treatmentId} />;
      case TASK_TAB:
      default:
        return <ControlPanelTaskTab sessionUUID={sessionId} treatmentUUID={treatmentId} />;
    }
  }, [activeTab, sessionId, treatmentId]);

  useEffect(() => {
    if (hasDevice && !isDeviceConnected) {
      setMediaIsStarted(false);
    }
  }, [isDeviceConnected, hasDevice, setMediaIsStarted]);

  const sendThemeCommand = async (value: boolean) => {
    if (!shouldSendThemeToBackend) {
      return;
    }
    try {
      await BackendService.sendThemeCommand(sessionId, treatmentId, value);
    } catch (e) {
      console.log('failed to send themeCommand');
    }
  };

  return (
    <div className="container">
      <Beforeunload onBeforeunload={triggerCloseOnUnity} />
      <ControlPanelHeader
        callHasStarted={isConnected}
        practitionerIsConnected={practitionerIsConnected}
        clientIsConnected={clientIsConnected}
        setClientIsConnected={setClientIsConnected}
        setPractitionerIsConnected={setPractitionerIsConnected}
        treatmentId={treatmentId}
        sessionId={sessionId}
        connectToRoom={ConnectToRoom}
        StopCall={StopCall}
        currentState={currentState}
        showThemeSelector={shouldShowThemeSelector}
      />

      <ControlPanelMainControls
        taskSnapoutClicked={taskSnapoutClicked}
        taskIsStarted={taskHasStarted}
        corrects={corrects}
        faults={faults}
        setTaskSnapoutClicked={handleSnapoutClicked}
        toggleIsStarted={handleToggleTaskClicked}
        isReactionTask={isReactionTask}
        autospeed={autoSpeed}
        setAutoSpeed={setAutoSpeed}
      />
      <div>
        <TabOptionsToogle onChangeHandler={handleClick} isActive={activeTab} />
      </div>
      <div className="tabs-container">{renderCorrectTab}</div>
      <ControlPanelFooter
        isCallActive={isCallActive}
        isGameLoading={gameIsLoading}
        sendThemeCommand={sendThemeCommand}
      />
    </div>
  );
};

export default ControlPanel;
